import { z } from "zod";
import {
    V2ReviewClientSchema,
    V2ReviewExpertSchema,
    V2ReviewProjectSchema,
    V2ModerationInfoSchema,
    V2ReviewResponseSchema,
} from "./common";
import { QueryParams } from "../../httpClient";
import { AgencyTypeFieldsSchema, AgencyTypeSchema } from "../shared";

// Admin Review Schema (full details)
export const AdminReviewSchema = z.object({
    id: z.number(),
    client: V2ReviewClientSchema,
    expert: V2ReviewExpertSchema,
    project: V2ReviewProjectSchema.nullable(),
    overall_rating: z.number(), // float 1-5
    quality: z.number().int().min(1).max(5), // Keep for future
    communication: z.number().int().min(1).max(5), // Keep for future
    timelines: z.number().int().min(1).max(5), // Keep for future
    value_for_money: z.number().int().min(1).max(5), // Keep for future
    review_text: z.string(),
    project_value_range_id: z.number().nullable(),
    project_value_range: z.string().nullable(),
    is_anonymous: z.boolean(),
    status: z.enum(["draft", "pending", "published", "unpublished"]),
    created_at: z.string(), // ISO8601 datetime string
    updated_at: z.string(), // ISO8601 datetime string
    moderation_info: V2ModerationInfoSchema,
    revision_count: z.number(),
    review_source: z.string(), // "Organic", "Invited", etc.
    imported_from: z.string().nullable().optional(), // Platform name when review was imported (e.g. "Upwork")
    hired_on_shopexperts_override: z.boolean().nullable().optional(), // Admin manual override value
    short_text_grandfathered: z.boolean().optional(), // Admin keeps short review text public
    public_text_hidden: z.boolean().optional(), // Left out of public lists under the current short-text settings
    is_hired_on_shopexperts: z.boolean().optional(), // Badge
    apps_and_themes: z.array(z.object({ // Icons display
        name: z.string(),
        icon_url: z.string().nullable().optional(),
    })).optional(),
});

// Client Review Schema (client's own reviews)
export const ClientReviewSchema = z.object({
    id: z.number(),
    expert_name: z.string(),
    expert_slug: z.string(),
    expert_type: z.enum(["freelancer", "agency"]),
    agency_type: AgencyTypeSchema.optional(),
    agency_type_fields: AgencyTypeFieldsSchema.optional(),
    expert_profile_picture: z.string().nullable(),
    overall_rating: z.number(), // float 1-5
    quality: z.number().int().min(1).max(5),
    communication: z.number().int().min(1).max(5),
    timelines: z.number().int().min(1).max(5),
    value_for_money: z.number().int().min(1).max(5),
    review_text: z.string(),
    is_anonymous: z.boolean(),
    project_id: z.number().nullable(),
    project_name: z.string().nullable(),
    status: z.enum(["draft", "pending", "published", "unpublished"]),
    created_at: z.string(), // ISO8601 datetime string
    updated_at: z.string(), // ISO8601 datetime string
    review_response: V2ReviewResponseSchema.nullable(),
});

// Expert Review Schema (reviews received by expert)
export const ExpertReviewSchema = z.object({
    id: z.number(),
    client_name: z.string(),
    client_company: z.string(),
    client_avatar_url: z.string().nullable(),
    client_company_website: z.string().nullable(),
    client_is_repeat_client: z.boolean(),
    is_anonymous: z.boolean(),
    overall_rating: z.number(), // float 1-5
    quality: z.number().int().min(1).max(5),
    communication: z.number().int().min(1).max(5),
    timelines: z.number().int().min(1).max(5),
    value_for_money: z.number().int().min(1).max(5),
    review_text: z.string(),
    project_id: z.number().nullable(),
    project_name: z.string().nullable(),
    project_value_range: z.string().nullable(),
    status: z.enum(["draft", "pending", "published", "unpublished"]),
    is_hired_on_shopexperts: z.boolean(),
    review_source: z.string(), // "Organic", "Invited", etc.
    apps_and_themes: z.array(z.object({
        name: z.string(),
        icon_url: z.string().nullable().optional(),
    })).optional(),
    created_at: z.string(), // ISO8601 datetime string
    updated_at: z.string(), // ISO8601 datetime string
    review_response: V2ReviewResponseSchema.nullable(),
});

// Public Review Schema (published reviews visible to public)
export const PublicReviewSchema = z.object({
    id: z.number(),
    client: z.object({
        name: z.string(),
        company: z.object({
            name: z.string(),
            url: z.string().optional().nullable(),
        }).optional(),
        is_repeat_client: z.boolean(),
    }),
    overall_rating: z.number(), // float 1-5
    ratings: z.object({
        quality: z.number().int().min(1).max(5),
        communication: z.number().int().min(1).max(5),
        timelines: z.number().int().min(1).max(5),
        value_for_money: z.number().int().min(1).max(5),
    }),
    content: z.string(),
    project_value_range: z.string().nullable(),
    review_source: z.string(), // "Organic", "Invited", etc.
    apps_and_themes: z.array(z.object({
        name: z.string(),
        icon_url: z.string().nullable().optional(),
    })).optional(),
    created_at: z.string(), // ISO8601 datetime string
    review_response: V2ReviewResponseSchema.nullable(),
    is_hired_on_shopexperts: z.boolean(),
    imported_from: z.string().nullable().optional(),
});

// Latest High-Rated Review Schema (for curated feed of top reviews)
export const LatestReviewExpertSchema = z.object({
    slug: z.string(),
    name: z.string(),
    type: z.enum(["freelancer", "agency"]),
    agency_type: AgencyTypeSchema.optional(),
    agency_type_fields: AgencyTypeFieldsSchema.optional(),
});

export const LatestReviewClientSchema = z.object({
    name: z.string(), // "FirstName L." or "Anonymous Merchant"
    company: z.object({
        name: z.string(), // Company name or "Anonymous"
        url: z.string().nullable(), // Company website URL (null if not set/anonymous)
    }),
    is_repeat_client: z.boolean(),
});

export const LatestReviewSchema = z.object({
    id: z.number(),
    client: LatestReviewClientSchema,
    is_hired_on_shopexperts: z.boolean(),
    is_repeat_client: z.boolean(),
    review_text: z.string(),
    review_date: z.string(), // ISO8601 datetime string
    overall_rating: z.number(), // float 4.5-5.0 (filtered)
    expert: LatestReviewExpertSchema,
});

export const LatestReviewsResponseSchema = z.array(LatestReviewSchema);

// Query Parameter Types
export interface ExpertReviewsQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
    status?: "published" | "pending" | "unpublished" | "draft";
}

export interface ClientReviewsQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
}

export interface AdminReviewsQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
    status?: "pending" | "published" | "unpublished" | "draft";
    expert_id?: number;
    client_id?: number;
    source?: "organic" | "requested" | "imported";
}

// Request Body Types
export interface RespondToReviewBody {
    response_text: string;
}

// Common review fields
interface ReviewFields {
    quality: number; // 1-5
    communication: number; // 1-5
    timelines: number; // 1-5
    value_for_money: number; // 1-5
    review_text: string; // min 10 characters
    is_anonymous?: boolean;
}

// Submit review by request ID (respond to review request)
export interface SubmitReviewByRequestIdBody extends ReviewFields {
    request_id: number;
}

// Submit review by expert ID (organic review)
export interface SubmitReviewByExpertIdBody extends ReviewFields {
    expert_id: number;
    idempotency_key: string;
    project_id?: number;
    project_value_range_id?: number;
}

// Submit review by token
export interface SubmitReviewByTokenBody extends ReviewFields {
    token: string;
    project_value_range_id?: number;
}

export interface UpdateReviewBody {
    is_anonymous?: boolean;
    quality?: number; // 1-5
    communication?: number; // 1-5
    timelines?: number; // 1-5
    value_for_money?: number; // 1-5
    review_text?: string; // min 10 characters
    project_value_range_id?: number;
}

export interface AdminUpdateReviewBody {
    quality?: number; // 1-5
    communication?: number; // 1-5
    timelines?: number; // 1-5
    value_for_money?: number; // 1-5
    review_text?: string; // min 10 characters
    project_value_range_id?: number | null;
    is_anonymous?: boolean;
    hired_on_shopexperts_override?: boolean;
}

export interface PublishReviewBody {
    moderation_notes?: string;
}

export interface UnpublishReviewBody {
    moderation_notes: string; 
}

export interface DeleteReviewBody {
    moderation_notes: string; 
}
